import type { Catalogs, PlainKey, Translate, Translation } from "@amtana/i18n/catalog";
import type { IntegrationId } from "@amtana/integration-contract";

// Copy for the integrations screen. The tools' names and what each one does come from the
// contract's list in English; the screen words them here, per tool, so they read in the
// active language.

export type IntegrationCopyKey = keyof typeof EN;
export type IntegrationCatalog = typeof EN;

const EN = {
  screenTitle: "Integrations · {product}",
  navIntegrations: "Integrations",
  breadcrumbHome: "Home",
  integrationsTitle: "Integrations",
  integrationsDescription: "Connect any tool for free",
  categoryProductivity: "Productivity",
  descriptionSmartlead: "Export leads to Smartlead with one click to boost cold email campaigns.",
  descriptionInstantly:
    "Connect Instantly and instantly receive your leads to enhance your mass email outreach.",
  descriptionLemlist: "Sync your leads with Lemlist to tailor and optimize your email campaigns.",
  descriptionMillionverifier:
    "Verify your email addresses with Million Verifier to ensure better deliverability and improve campaign quality.",
  descriptionClaude:
    "Operate the platform from Claude: search accounts, run analyses, manage prospects, lists, campaigns and your inbox, all by chat.",
  connectTitle: "Connect {name}",
  connectKeyDescription: "Paste the API key from your {name} account settings.",
  connectMcpDescription: "We'll create an MCP server address to add as a connector in Claude.",
  cancel: "Cancel",
  connectApp: "Connect App",
  connected: "Connected",
  connectedWithHint: "Connected · {hint}",
  connectedToast: "{name} connected",
  couldNotConnect: "Could not connect {name}",
  apiKeyLabel: "API key",
  apiKeyHint: "Simulated: the key is checked for shape only and never leaves the server.",
  claudeHelp:
    "Ask Claude to analyze accounts, filter prospects and prepare campaigns without leaving the chat.",
  mcpAddressTitle: "MCP server address",
  disconnect: "Disconnect",
  disconnectedToast: "{name} disconnected",
  couldNotDisconnect: "Could not disconnect {name}",
} as const;

const ES: Translation<IntegrationCatalog> = {
  screenTitle: "Integraciones · {product}",
  navIntegrations: "Integraciones",
  breadcrumbHome: "Inicio",
  integrationsTitle: "Integraciones",
  integrationsDescription: "Conecta cualquier herramienta gratis",
  categoryProductivity: "Productividad",
  descriptionSmartlead: "Exporta leads a Smartlead con un clic para impulsar tus campañas de correo en frío.",
  descriptionInstantly:
    "Conecta Instantly y recibe tus leads al instante para mejorar tu alcance de correo masivo.",
  descriptionLemlist: "Sincroniza tus leads con Lemlist para adaptar y optimizar tus campañas de correo.",
  descriptionMillionverifier:
    "Verifica tus correos con Million Verifier para lograr mejor entregabilidad y más calidad en tus campañas.",
  descriptionClaude:
    "Maneja la plataforma desde Claude: busca cuentas, ejecuta análisis y gestiona prospectos, listas, campañas y tu bandeja, solo con el chat.",
  connectTitle: "Conectar {name}",
  connectKeyDescription: "Pega la clave de API desde los ajustes de tu cuenta de {name}.",
  connectMcpDescription: "Crearemos una dirección de servidor MCP para agregarla como conector en Claude.",
  cancel: "Cancelar",
  connectApp: "Conectar app",
  connected: "Conectada",
  connectedWithHint: "Conectada · {hint}",
  connectedToast: "{name} conectada",
  couldNotConnect: "No pudimos conectar {name}",
  apiKeyLabel: "Clave de API",
  apiKeyHint: "Simulado: solo se revisa el formato de la clave y nunca sale del servidor.",
  claudeHelp: "Pídele a Claude que analice cuentas, filtre prospectos y prepare campañas sin salir del chat.",
  mcpAddressTitle: "Dirección del servidor MCP",
  disconnect: "Desconectar",
  disconnectedToast: "{name} desconectada",
  couldNotDisconnect: "No pudimos desconectar {name}",
};

export const INTEGRATION_CATALOGS: Catalogs<IntegrationCatalog> = { en: EN, es: ES };

const DESCRIPTION_KEY: Readonly<Record<IntegrationId, PlainKey<IntegrationCatalog>>> = {
  smartlead: "descriptionSmartlead",
  instantly: "descriptionInstantly",
  lemlist: "descriptionLemlist",
  millionverifier: "descriptionMillionverifier",
  claude: "descriptionClaude",
};

/** What a tool does, in the active language. */
export function integrationDescription(id: IntegrationId, t: Translate<IntegrationCatalog>): string {
  return t(DESCRIPTION_KEY[id]);
}

/** A tool's category in the active language; one the screen has no words for shows as it is. */
export function integrationCategory(category: string, t: Translate<IntegrationCatalog>): string {
  return category === "Productivity" ? t("categoryProductivity") : category;
}
